// 하단 수량 차트 (매물수 vs 실거래건수) — variant='sale' | 'lease'
import React, { useMemo } from 'react';
import ChartCard from './ChartCard';
import { METRIC_COLORS, COUNT_TOGGLE_KEYS, COUNT_BRUSH, COUNT_OPTION_PREFIX } from './constants';
import {
  toNumber, toTimestamp, darkenHex,
  getEndLabelStyle, buildSyncedDataZoom, formatNumber, escapeRegExp, safeTooltipValueFormatter,
} from './utils';

const COUNT_CONFIG = {
  sale: { listingKey: 'saleCount', realKey: 'saleRealCount', listingLabel: '매매 매물수', realLabel: '매매 실거래건수', title: '매매 수량' },
  lease: { listingKey: 'leaseCount', realKey: 'leaseRealCount', listingLabel: '전세 매물수', realLabel: '전세 실거래건수', title: '전세 수량' },
};

// 수량 차트 ECharts 옵션 빌더 (순수 함수)
export function createCountOption({ filteredItems, colorByCode, metricVisible, syncedZoom, variant, optionPrefix }) {
  const { listingKey, realKey, listingLabel, realLabel } = COUNT_CONFIG[variant];
  const isSingleComplex = filteredItems.length === 1;

  const dateSet = new Set();
  filteredItems.forEach((item) => {
    item.weeklySnapshots.forEach((row) => {
      if (row?.baseDate) dateSet.add(row.baseDate);
    });
  });
  const countDates = Array.from(dateSet).sort((a, b) => toTimestamp(a) - toTimestamp(b));

  const series = [];
  filteredItems.forEach((item) => {
    const rowMap = new Map(item.weeklySnapshots.map((row) => [row.baseDate, row]));
    const baseColor = colorByCode.get(item.compareKey ?? item.naverDanjiCode) || '#334155';
    const listingColor = isSingleComplex ? (METRIC_COLORS[listingKey] || baseColor) : baseColor;
    const realColor = isSingleComplex ? (METRIC_COLORS[realKey] || darkenHex(baseColor, 0.35)) : darkenHex(baseColor, 0.35);
    const name = item.displayName || item.buildingName || item.naverDanjiCode;
    const listingData = countDates.map((date) => toNumber(rowMap.get(date)?.[listingKey]));
    const realData = countDates.map((date) => toNumber(rowMap.get(date)?.[realKey]));

    if (metricVisible[listingKey]) {
      series.push({
        name: `${name} ${listingLabel}`,
        type: 'bar',
        data: listingData,
        barMaxWidth: 12,
        itemStyle: { color: `${listingColor}99`, borderRadius: [3, 3, 0, 0] },
        emphasis: { focus: 'series' },
        z: 2,
        endLabel: isSingleComplex ? getEndLabelStyle('매물수', listingColor) : undefined,
        labelLayout: isSingleComplex ? { moveOverlap: 'shiftY' } : undefined,
      });
    }

    if (metricVisible[realKey]) {
      series.push({
        name: `${name} ${realLabel}`,
        type: 'bar',
        data: realData,
        barMaxWidth: 12,
        itemStyle: { color: `${realColor}99`, borderRadius: [3, 3, 0, 0] },
        emphasis: { focus: 'series' },
        z: 3,
        endLabel: isSingleComplex ? getEndLabelStyle('실거래건수', realColor) : undefined,
        labelLayout: isSingleComplex ? { moveOverlap: 'shiftY' } : undefined,
      });
    }
  });

  return {
    animationDuration: 260,
    // 제목은 HTML 드롭다운(ChartCard)로 렌더. 수량 차트는 범례 없음.
    // 그리드: 상단 가격차트와 동일 left/right 로 폭(플롯영역) 일치
    grid: { left: 56, right: isSingleComplex ? 132 : 40, top: 14, bottom: 30 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      backgroundColor: 'rgba(255,255,255,0.98)',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      textStyle: { color: '#0f172a' },
      extraCssText: 'box-shadow: 0 8px 24px rgba(15,23,42,.12);',
      // 기본 마크업의 addCommas 경로 우회 (스택 오버플로 방지)
      valueFormatter: safeTooltipValueFormatter,
      formatter: (params) => {
        if (!Array.isArray(params) || params.length === 0) return '';
        const date = params[0]?.axisValue || '';
        const metricPattern = new RegExp(` (${escapeRegExp(listingLabel)}|${escapeRegExp(realLabel)})$`);
        const grouped = new Map();

        params.forEach((param) => {
          const rawName = String(param.seriesName || '');
          const value = Array.isArray(param.value) ? param.value[1] : param.value;
          const complexName = rawName.replace(metricPattern, '');
          const metric = rawName.endsWith(` ${listingLabel}`) ? listingLabel : rawName.endsWith(` ${realLabel}`) ? realLabel : '';
          if (!metric) return;
          if (!grouped.has(complexName)) {
            grouped.set(complexName, { complexName, listing: null, real: null, listingColor: '#64748b', realColor: '#64748b' });
          }
          const row = grouped.get(complexName);
          if (metric === listingLabel) { row.listing = value; row.listingColor = param.color; }
          else { row.real = value; row.realColor = param.color; }
        });

        let html = `<div style="font-weight:700;margin-bottom:8px;">${date}</div>`;
        html += '<table style="border-collapse:collapse;min-width:360px;">';
        html += '<thead><tr>';
        html += '<th style="text-align:left;padding:4px 8px;color:#475569;font-size:11px;">단지</th>';
        html += `<th style="text-align:right;padding:4px 8px;color:#475569;font-size:11px;">${listingLabel}</th>`;
        html += `<th style="text-align:right;padding:4px 8px;color:#475569;font-size:11px;">${realLabel}</th>`;
        html += '</tr></thead><tbody>';
        Array.from(grouped.values()).forEach((row) => {
          html += '<tr>';
          html += `<td style="padding:4px 8px;font-weight:700;color:#0f172a;">${row.complexName}</td>`;
          html += `<td style="padding:4px 8px;text-align:right;color:${row.listingColor};font-weight:700;">${formatNumber(row.listing)}</td>`;
          html += `<td style="padding:4px 8px;text-align:right;color:${row.realColor};font-weight:700;">${formatNumber(row.real)}</td>`;
          html += '</tr>';
        });
        html += '</tbody></table>';
        return html;
      },
    },
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    legend: { show: false },
    xAxis: {
      type: 'category',
      data: countDates,
      axisLabel: {
        formatter: (value) => {
          const text = String(value || '');
          return text.length >= 10 ? `${text.slice(2, 4)}.${text.slice(5, 7)}` : text;
        },
        color: '#64748b',
      },
      axisLine: { lineStyle: { color: '#cbd5e1' } },
    },
    yAxis: {
      type: 'value',
      axisLabel: { formatter: (value) => Number(value).toLocaleString('ko-KR'), color: '#64748b' },
      splitLine: { lineStyle: { color: '#eef2f7' } },
    },
    toolbox: { show: false }, // 영역지정(브러시) 버튼 등 툴박스 UI 숨김
    brush: {
      // 버튼 없이 takeGlobalCursor 로만 활성화(자동 on)
      toolbox: ['lineX', 'clear'],
      xAxisIndex: 0,
      brushStyle: { borderWidth: 2, color: 'rgba(59, 130, 246, 0.2)', borderColor: 'rgba(59, 130, 246, 0.8)' },
      outOfBrush: { colorAlpha: 0.4 },
    },
    dataZoom: buildSyncedDataZoom(syncedZoom, optionPrefix),
    series,
  };
}

// 수량 차트 컴포넌트
export default function CountChart({
  variant, // 'sale' | 'lease'
  panelPrefix, // 위치(상단 동반/하단 단독)에 따라 다른 prefix 주입
  wrapperClassName = 'ranking-compare-chart-section',
  filteredItems,
  colorByCode,
  metricVisible,
  syncedZoom,
  brushRangeRows,
  onToggle,
  onBrushClose,
  chartRef,
  chartEvents,
  height,
}) {
  const option = useMemo(
    () => createCountOption({
      filteredItems, colorByCode, metricVisible, syncedZoom, variant,
      optionPrefix: COUNT_OPTION_PREFIX[variant],
    }),
    [filteredItems, colorByCode, metricVisible, syncedZoom, variant],
  );

  // 제목 드롭다운(매물수 ↔ 실거래건수) — 클릭으로 전환(배타)
  const cfg = COUNT_CONFIG[variant];
  const titleDropdown = {
    options: [
      { key: cfg.listingKey, label: cfg.listingLabel },
      { key: cfg.realKey, label: cfg.realLabel },
    ],
    activeKey: metricVisible[cfg.listingKey] ? cfg.listingKey : cfg.realKey,
    onChange: (key) => onToggle(key, true),
  };

  return (
    <ChartCard
      wrapperClassName={wrapperClassName}
      panelPrefix={panelPrefix}
      titleDropdown={titleDropdown}
      brushTitle={COUNT_BRUSH[variant].title}
      brushMetrics={COUNT_BRUSH[variant].metrics}
      brushRangeRows={brushRangeRows}
      metricVisible={metricVisible}
      onBrushClose={onBrushClose}
      toggleKeys={COUNT_TOGGLE_KEYS[variant]}
      onToggle={onToggle}
      exclusiveToggles
      option={option}
      chartRef={chartRef}
      height={height}
      chartEvents={chartEvents}
    />
  );
}
